import { FC, useContext } from 'react';

import { useRouter } from 'next/router';
import Link from 'next/link';

import { i18n as I18n } from 'utils/i18n';
import { AuthContext } from '../../state/auth';

import ModalDialog from '../modal';

export type ModalType =
  | 'sign-in'
  | 'space-type-clinical-error'
  | 'space-type-non-clinical-error'
  | 'service-clash'
  | 'add-to-booking-failure'
  | 'space-count-warning'
  | 'unapproved-user-warning'
  | 'no-service-error'
  | 'no-space-error';

interface Props {
  openModal: ModalType | null;
  onClose: () => void;
}

const SpaceListModals: FC<Props> = ({ openModal, onClose: close }) => {
  const router = useRouter();
  const { actions: { setReturnUrl } = {} } = useContext(AuthContext);

  return (
    <>
      <ModalDialog
        isOpen={openModal === 'sign-in'}
        onClose={close}
        showCloseIcon
        headerLabel="Your Booking"
        size="small"
        primaryAction={
          <Link href={I18n.t('navigation.register.url')}>
            <a className="button button--primary">
              {I18n.t('space_list.modal.register')}
            </a>
          </Link>
        }
        secondaryAction={
          <Link href={I18n.t('navigation.sign_in.url')} passHref>
            <a
              className="button button--secondary"
              onClick={() => setReturnUrl(router.asPath)}
            >
              {I18n.t('space_list.modal.sign_in')}
            </a>
          </Link>
        }
      >
        <p>{I18n.t('space_list.modal.paragraph_one')}</p>
        <p>{I18n.t('space_list.modal.paragraph_two')}</p>
      </ModalDialog>

      <ModalDialog
        isOpen={openModal === 'space-type-clinical-error'}
        onClose={close}
        showCloseIcon
        headerLabel={I18n.t('search_results.service_clash.header')}
        size="xsmall"
        primaryAction={
          <a className="button button--secondary" onClick={close}>
            {I18n.t('search_results.modal.close')}
          </a>
        }
      >
        <p>{I18n.t('search_results.errors.space_type.clinical')}</p>
      </ModalDialog>

      <ModalDialog
        isOpen={openModal === 'space-type-non-clinical-error'}
        onClose={close}
        showCloseIcon
        headerLabel={I18n.t('search_results.service_clash.header')}
        size="xsmall"
        primaryAction={
          <a className="button button--secondary" onClick={close}>
            {I18n.t('search_results.modal.close')}
          </a>
        }
      >
        <p>{I18n.t('search_results.errors.space_type.non_clinical')}</p>
      </ModalDialog>

      <ModalDialog
        isOpen={openModal === 'service-clash'}
        onClose={close}
        showCloseIcon
        headerLabel={I18n.t('search_results.service_clash.header')}
        size="xsmall"
        primaryAction={
          <a className="button button--secondary" onClick={close}>
            {I18n.t('search_results.modal.close')}
          </a>
        }
      >
        <p>{I18n.t('search_results.service_clash.message')}</p>
      </ModalDialog>

      <ModalDialog
        isOpen={openModal === 'add-to-booking-failure'}
        onClose={close}
        showCloseIcon={false}
        headerLabel={I18n.t('search_results.modal.header')}
        size="xsmall"
        primaryAction={
          <a className="button button--secondary" onClick={close}>
            {I18n.t('search_results.modal.close')}
          </a>
        }
      >
        <p className="message">{I18n.t('search_results.modal.message')}</p>
      </ModalDialog>

      <ModalDialog
        isOpen={openModal === 'space-count-warning'}
        onClose={close}
        headerLabel={I18n.t('quick_book.maximum_spaces.title')}
        size="xsmall"
        primaryAction={
          <button className="button button--primary" onClick={close}>
            {I18n.t('shared.ok')}
          </button>
        }
      >
        <p data-testid="maximumSpacesReached">
          {I18n.t('quick_book.maximum_spaces.message')}
        </p>
      </ModalDialog>

      <ModalDialog
        isOpen={openModal === 'unapproved-user-warning'}
        onClose={close}
        headerLabel={I18n.t('quick_book.unapproved_user.title')}
        size="xsmall"
        primaryAction={
          <button className="button button--primary" onClick={close}>
            {I18n.t('shared.ok')}
          </button>
        }
      >
        <p data-testid="unapprovedUser">
          {I18n.t('quick_book.unapproved_user.message')}
        </p>
      </ModalDialog>

      <ModalDialog
        isOpen={openModal === 'no-service-error'}
        onClose={close}
        showCloseIcon={false}
        headerLabel={I18n.t('search_results.modal.header')}
        size="xsmall"
        primaryAction={
          <a className="button button--secondary" onClick={close}>
            {I18n.t('search_results.modal.close')}
          </a>
        }
      >
        <p className="message">{I18n.t('venue_calendar.no_service.message')}</p>
      </ModalDialog>

      <ModalDialog
        isOpen={openModal === 'no-space-error'}
        onClose={close}
        showCloseIcon={false}
        headerLabel={I18n.t('search_results.modal.header')}
        size="xsmall"
        primaryAction={
          <a className="button button--secondary" onClick={close}>
            {I18n.t('search_results.modal.close')}
          </a>
        }
      >
        <p className="message">{I18n.t('venue_calendar.no_space.message')}</p>
      </ModalDialog>
    </>
  );
};

export default SpaceListModals;
